import {
    AfterViewInit,
    Component,
    ElementRef,
    Injector,
    OnDestroy,
    OnInit,
    ViewChild,
    NgZone,
} from '@angular/core';
import { DynamicBaseClass } from '../dynamic-base-class/dynamic-base-class.component';
import {
    distinctUntilChanged,
    interval,
    Observable,
    Subscription,
    take,
    takeUntil,
} from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/app/controls/confirm-dialog/confirm-dialog.component';
import isEqual from 'lodash-es/isEqual';

type TableColumn = {
    type?: string;
    dataStoreKey?: string;
    headerTitle?: string;
    visible?: boolean;
    icon?: string;
    dropDown?: DropDownConfig;
};

type DropDownConfig = {
    dataStore?: string;
    dataStore$?: Observable<any[]>;
    dataStoreKeyForValue?: string;
    dataStoreKeyForDisplay?: string;
    withEmptyFirstLine?: boolean;
    filter?: {
        dataStoreKey: string;
        filterValueDataStoreKey: string;
    };
};

@Component({
    standalone: false,
    selector: 'app-dynamic-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
})
export class TableComponent
    extends DynamicBaseClass
    implements OnInit, OnDestroy, AfterViewInit
{
    public value$: Observable<any>;
    public filterStoreData$: Observable<any>;

    @ViewChild('tableVisibility', { static: false })
    tableVisibility: ElementRef<HTMLDivElement>;
    @ViewChild('table', { read: ElementRef })
    matTableEl!: ElementRef<HTMLElement>;

    storeData: string;
    filterStoreData: string;
    filterResultStoreData: string;
    dataStoreKeyForId: string;
    maxHeight: string;
    withPagination: boolean;
    withFilter: boolean;
    withSelection: boolean;
    withRemove: boolean;
    withAllRemove: boolean;
    withOperation: boolean;
    withAutoScroll: boolean;
    withDragDrop: boolean = false;

    columns: TableColumn[];
    paginatorPageSize: number = 10;
    paginatorPageSizeOptions: number[] = [5, 10, 25, 100];

    intervalInSecondToExecuteActions: number;
    intervalExecuteActions: string[];

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    dataSource: MatTableDataSource<Element>;
    keyPath: string;
    displayPath: string;

    displayedColumns: string[] = [];
    selection: SelectionModel<any>;
    selectedStoreData: string;

    private rafId: number | null = null;
    private lastTs = 0;
    private speedPxPerSec = 40; // anpassen
    private waiting = true;

    readonly autoScrollTimeInMilliSeconds: number = 5000;

    constructor(
        private dialog: MatDialog,
        injector: Injector,
        private zone: NgZone,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        if (this.config) {
            this.storeData = this.config.storeData;
            this.filterStoreData = this.config.filterStoreData;
            this.filterResultStoreData = this.config.filterResultStoreData;
            this.selectedStoreData = this.config.selectedStoreData;
            this.dataStoreKeyForId = this.config.dataStoreKeyForId;

            this.maxHeight = this.config.maxHeight;
            this.withPagination = this.config.withPagination ?? false;
            this.withFilter = this.config.withFilter ?? false;
            this.withSelection = this.config.withSelection ?? false;
            this.withRemove = this.config.withRemove ?? false;
            this.withAllRemove = this.config.withAllRemove ?? false;
            this.withOperation = this.config.withOperation ?? false;
            this.withAutoScroll = this.config.withAutoScroll ?? false;
            this.withDragDrop = this.config.withDragDrop ?? false;

            this.paginatorPageSize = this.config.paginatorPageSize ?? 10;
            this.paginatorPageSizeOptions = this.config
                .paginatorPageSizeOptions ?? [5, 10, 25, 100];

            this.intervalInSecondToExecuteActions =
                this.config.intervalInSecondToExecuteActions;
            this.intervalExecuteActions = this.config.intervalExecuteActions;

            if (
                this.config.automaticColumns &&
                this.config.automaticColumns === true
            ) {
                if (this.config.value) {
                    this.columns = Object.keys(this.config.value[0]).map(
                        (key) => {
                            return {
                                dataStoreKey: key,
                                headerTitle: key,
                            };
                        },
                    );
                    this.displayedColumns = Object.keys(this.config.value[0]);
                }
            } else if (this.config.columns) {
                this.columns = this.config.columns;
                this.displayedColumns = this.columns
                    .filter((c) => c.visible ?? false)
                    .map((c) => c.dataStoreKey);
            }
            if (this.withDragDrop) {
                this.displayedColumns = ['drag', ...this.displayedColumns];
            }
            if (this.withSelection) {
                this.displayedColumns = ['select', ...this.displayedColumns];
            }
            if (this.withOperation) {
                this.displayedColumns = [...this.displayedColumns, 'operation'];
            }
            if (this.withRemove) {
                this.displayedColumns = [...this.displayedColumns, 'remove'];
            }

            // DropDown DataSources initialisieren
            this.columns?.forEach((column) => {
                if (
                    column.type.toLowerCase() === 'dropdown' &&
                    column.dropDown &&
                    column.dropDown.dataStore
                ) {
                    //console.log('Lade DropDown Daten für Spalte ' + column.dataStoreKey + ' aus ' + column.dropDown.dataStore);
                    column.dropDown.dataStore$ =
                        this.dynamicUiServices.GetValueAsObservable(
                            column.dropDown.dataStore,
                            this.storeDataChildNames,
                        );
                }
            });
        }

        this.dataSource = new MatTableDataSource<any>([]);
        this.selection = new SelectionModel<any>(this.withSelection, []);
        this.selection.changed.subscribe((selection) => {
            //console.log('Selection changed: ', selection);
            if (this.selectedStoreData) {
                if (!selection.source.isMultipleSelection()) {
                    this.dynamicUiServices.SetValue(
                        this.selectedStoreData,
                        this.storeDataChildNames,
                        selection.source.selected[0],
                    );
                } else {
                    this.dynamicUiServices.SetValue(
                        this.selectedStoreData,
                        this.storeDataChildNames,
                        selection.source.selected,
                    );
                }
            }
        });

        this.value$ = this.GetValueFromConfig();
        this.value$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            //console.log('Table Value changed', value);
            this.selection.clear();
            if (
                this.config.automaticColumns &&
                (this.config.automaticColumns as boolean) === true
            ) {
                if (value && value.length > 0) {
                    this.columns = Object.keys(value[0]).map((key) => {
                        return {
                            dataStoreKey: key,
                            headerTitle: key,
                        };
                    });
                    this.displayedColumns = Object.keys(value[0]);
                } else {
                    this.columns = [];
                    this.displayedColumns = [];
                }
            }

            if (!this.filterStoreData) {
                // HACK: Vergleich der alten und neuen Werte als JSON-String, um unnötige Aktualisierungen der DataSource zu vermeiden (z.B. wegen Referenzänderungen), da dies die Performance bei großen Tabellen stark beeinträchtigen kann
                //if (JSON.stringify(value ?? []) === JSON.stringify(this.dataSource.data ?? [])) return;
                //this.dataSource = new MatTableDataSource<any>(value);
                this.dataSource.data = value ?? [];
            } else {
                var filter = this.dynamicUiServices.GetValue(
                    this.filterStoreData,
                    this.storeDataChildNames,
                );
                // HACK: Vergleich der alten und neuen Werte als JSON-String, um unnötige Aktualisierungen der DataSource zu vermeiden (z.B. wegen Referenzänderungen), da dies die Performance bei großen Tabellen stark beeinträchtigen kann
                //if (JSON.stringify(this.filterData(value, filter) ?? []) === JSON.stringify(this.dataSource.data ?? [])) return;
                //this.dataSource = new MatTableDataSource<any>(this.filterData(value, filter));
                this.dataSource.data = this.filterData(value, filter);
            }
        });
        if (this.filterStoreData) {
            this.filterStoreData$ = this.dynamicUiServices.GetValueAsObservable(
                this.filterStoreData,
                this.storeDataChildNames,
            );
            this.filterStoreData$
                .pipe(takeUntil(this.destroy$))
                .subscribe((filter) => {
                    var value = this.dynamicUiServices.GetValue(
                        this.storeData,
                        this.storeDataChildNames,
                    );
                    // HACK: Vergleich der alten und neuen Werte als JSON-String, um unnötige Aktualisierungen der DataSource zu vermeiden (z.B. wegen Referenzänderungen), da dies die Performance bei großen Tabellen stark beeinträchtigen kann
                    //if (JSON.stringify(this.filterData(value, filter) ?? []) === JSON.stringify(this.dataSource.data ?? [])) return;
                    //this.dataSource = new MatTableDataSource<any>(this.filterData(value, filter));
                    this.dataSource.data = this.filterData(value, filter);
                    this.selection.clear();
                    if (this.filterResultStoreData) {
                        this.dynamicUiServices.SetValue(
                            this.filterResultStoreData,
                            this.storeDataChildNames,
                            this.dataSource.data,
                        );
                    }
                });
        }

        if (this.selectedStoreData) {
            this.dynamicUiServices
                .GetValueAsObservable(
                    this.selectedStoreData,
                    this.storeDataChildNames,
                )
                .subscribe((c) => {
                    if (this.withSelection) {
                        if (c && Array.isArray(c) && c.length > 0) {
                            // if (this.selection != c) {
                            //     this.selection = c;
                            // }
                        } else {
                            // this.selectedValues = [];
                            // if (this.select?.options && this.select.options.length > 0) {
                            //     this.select.options.forEach((item: MatOption) => item.deselect());
                            // }
                        }
                    } else {
                        if (c) {
                            // if (this.selectedValue != c) {
                            //     this.selectedValue = c;
                            // }
                        } else {
                            // this.selectedValue = "";
                        }
                    }
                });
        }

        /* Intervall für die Ausführung von Aktionen */
        if (
            this.intervalExecuteActions &&
            this.intervalExecuteActions.length > 0 &&
            this.intervalInSecondToExecuteActions
        ) {
            const intervalInSeconds =
                this.intervalInSecondToExecuteActions * 1000;
            this.subscription$.add(
                interval(intervalInSeconds)
                    .pipe()
                    .subscribe(() => {
                        if (
                            !this.isHidden(this.tableVisibility.nativeElement)
                        ) {
                            if (
                                this.intervalExecuteActions &&
                                this.intervalExecuteActions.length > 0
                            ) {
                                this.intervalExecuteActions.forEach(
                                    (actionName) => {
                                        this.executeAction(
                                            actionName,
                                            'ExecuteOnInterval: ' + actionName,
                                        );
                                    },
                                );
                            }
                        }
                    }),
            );
        }

        super.GlobalConfig();
    }

    private filterData(value: any[], filter: any): any {
        var result = [...(value ?? [])];
        if (filter && result.length > 0) {
            if (filter) {
                Object.getOwnPropertyNames(filter).forEach((property) => {
                    if (filter[property]) {
                        if (Array.isArray(filter[property])) {
                            if (filter[property].length > 0) {
                                result = result.filter((entry) => {
                                    if (
                                        Object.getOwnPropertyNames(
                                            entry,
                                        ).includes(property)
                                    ) {
                                        if (
                                            filter[property].includes(
                                                entry[property],
                                            )
                                        ) {
                                            return entry;
                                        }
                                    }
                                });
                            }
                        } else {
                            result = result.filter((entry) => {
                                if (
                                    Object.getOwnPropertyNames(entry).includes(
                                        property,
                                    )
                                ) {
                                    if (entry[property] === filter[property]) {
                                        return entry;
                                    }
                                }
                            });
                        }
                    }
                });
            }
        }
        return result;
    }

    ngOnDestroy(): void {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        super.ngOnDestroy();
    }

    ngAfterViewInit(): void {
        if (this.withPagination) {
            this.dataSource.paginator = this.paginator;
        }
        this.dataSource.sort = this.sort;

        if (this.withAutoScroll) {
            // Start außerhalb der Angular Zone für bessere Performance
            this.zone.runOutsideAngular(() => {
                // initial 2s warten bevor das erste Scrollen beginnt
                setTimeout(() => {
                    this.lastTs = performance.now();
                    this.waiting = false;
                    this.rafId = requestAnimationFrame(this.scrollStep);
                }, 2000);
            });
        }
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();
        this.selection.clear();
    }

    clearFilter(filterElement: HTMLInputElement) {
        filterElement.value = '';
        this.dataSource.filter = '';
        this.selection.clear();
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.filteredData.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    toggleRow(row: Element) {
        if (this.withSelection) {
            this.selection.toggle(row);
        } else {
            this.selection.select(row);
        }
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    toggleAllRows() {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }
        this.selection.select(...this.dataSource.filteredData);
    }

    /** The label for the checkbox on the passed row */
    checkboxLabel(row?: Element): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row`;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    removeRow(row: Element, rowIndex: number) {
        console.log('Remove Row: ', row, rowIndex);
        if (this.withRemove) {
            if (this.withOperation) {
                if (this.withDragDrop) {
                    const values = [
                        ...(this.dynamicUiServices.GetValue(
                            this.storeData,
                            this.storeDataChildNames,
                        ) as any[]),
                    ];
                    values.splice(rowIndex, 1);
                    this.dynamicUiServices.SetValue(
                        this.storeData,
                        this.storeDataChildNames,
                        values,
                    );
                } else {
                    const values = this.dynamicUiServices.GetValue(
                        this.storeData,
                        this.storeDataChildNames,
                    ) as any[];
                    values.forEach((destRow) => {
                        if (JSON.stringify(destRow) === JSON.stringify(row)) {
                            if (destRow.operation === 'add') {
                                destRow.operation = 'delete';
                            } else if (destRow.operation === 'remove') {
                                destRow.operation = '';
                            } else {
                                destRow.operation = 'remove';
                            }
                        }
                    });
                    const newValues =
                        (values.filter(
                            (item) => item.operation !== 'delete',
                        ) as any[]) ?? [];
                    this.dynamicUiServices.SetValue(
                        this.storeData,
                        this.storeDataChildNames,
                        newValues,
                    );
                }
            } else {
                if (this.withDragDrop) {
                    const values = [
                        ...(this.dynamicUiServices.GetValue(
                            this.storeData,
                            this.storeDataChildNames,
                        ) as any[]),
                    ];
                    values.splice(rowIndex, 1);
                    this.dynamicUiServices.SetValue(
                        this.storeData,
                        this.storeDataChildNames,
                        values,
                    );
                } else {
                    var values = [
                        ...(this.dynamicUiServices.GetValue(
                            this.storeData,
                            this.storeDataChildNames,
                        ) as any[]),
                    ];
                    var index = values.indexOf(row);
                    if (index > -1) {
                        values.splice(index, 1);
                    }
                    this.dynamicUiServices.SetValue(
                        this.storeData,
                        this.storeDataChildNames,
                        values,
                    );
                }
            }
        }
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    removeAll() {
        if (this.withRemove) {
            const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
                data: {
                    title: 'Alle Einträge entfernen',
                    message:
                        'Sind Sie sicher, dass Sie alle Einträge entfernen möchten?',
                },
            });
            confirmDialog.afterClosed().subscribe((result) => {
                if (result === true) {
                    if (this.withOperation) {
                        const values = this.dynamicUiServices.GetValue(
                            this.storeData,
                            this.storeDataChildNames,
                        ) as any[];
                        values.forEach((row) => {
                            if (row.operation === 'add') {
                                row.operation = 'delete';
                            } else {
                                row.operation = 'remove';
                            }
                        });
                        const newValues =
                            (values.filter(
                                (item) => item.operation !== 'delete',
                            ) as any[]) ?? [];
                        this.dynamicUiServices.SetValue(
                            this.storeData,
                            this.storeDataChildNames,
                            newValues,
                        );
                    } else {
                        this.dynamicUiServices.SetValue(
                            this.storeData,
                            this.storeDataChildNames,
                            [],
                        );
                    }
                }
            });
        }
    }

    onDropDownModelChange(
        event: any,
        column: TableColumn,
        row: any,
        rowIndex: number,
    ) {
        //console.log(event, column, element);
        //this.dynamicFacade.GetData("local", "members", ".").pipe(take(1)).subscribe(c => console.log('DropDown Model Change: ', c));

        if (this.withDragDrop) {
            console.log(
                'DropDown Model Change with DragDrop: ',
                event,
                column,
                row,
                rowIndex,
            );
            var values = [
                ...(this.dynamicUiServices.GetValue(
                    this.storeData,
                    this.storeDataChildNames,
                ) as any[]),
            ];
            values[rowIndex][column.dataStoreKey] = event;
            if (this.withOperation) {
                if (values[rowIndex].operation !== 'add') {
                    values[rowIndex].operation = 'update';
                }
            }
            this.dynamicUiServices.SetValue(
                this.storeData,
                this.storeDataChildNames,
                values,
            );
        } else {
            this.dynamicUiServices.SetValueInArrayWithId(
                this.storeData,
                Object.keys(row).find(
                    (key) =>
                        key.toLowerCase() ===
                        this.dataStoreKeyForId.toLowerCase(),
                ),
                row[
                    Object.keys(row).find(
                        (key) =>
                            key.toLowerCase() ===
                            this.dataStoreKeyForId.toLowerCase(),
                    )
                ],
                Object.keys(row).find(
                    (key) =>
                        key.toLowerCase() === column.dataStoreKey.toLowerCase(),
                ),
                event,
                this.storeDataChildNames,
                'DropDownChange:' + column.dataStoreKey,
            );
            if (this.withOperation) {
                this.dynamicUiServices.SetValueInArrayWithId(
                    this.storeData,
                    Object.keys(row).find(
                        (key) =>
                            key.toLowerCase() ===
                            this.dataStoreKeyForId.toLowerCase(),
                    ),
                    row[
                        Object.keys(row).find(
                            (key) =>
                                key.toLowerCase() ===
                                this.dataStoreKeyForId.toLowerCase(),
                        )
                    ],
                    'operation',
                    row.operation === 'add' ? 'add' : 'update',
                    this.storeDataChildNames,
                    'DropDownChange: Operation changed to ' +
                        (row.operation === 'add' ? 'add' : 'update'),
                );
            }
        }
    }

    dropRow(event: CdkDragDrop<any[]>): void {
        const data = [
            ...((this.dynamicUiServices.GetValue(
                this.storeData,
                this.storeDataChildNames,
            ) as any[]) ?? []),
        ];
        moveItemInArray(data, event.previousIndex, event.currentIndex);
        this.dynamicUiServices.SetValue(
            this.storeData,
            this.storeDataChildNames,
            data,
        );
    }

    IsDropDownItemVisible(column: TableColumn, item: any, row: any): boolean {
        if (column.dropDown.filter) {
            //console.log('Filtere DropDown Item ', row[column.dropDown.filter.dataStoreKey], ' mit Wert ', column.dropDown.filter.filterValueDataStoreKey, ' = ', item[column.dropDown.filter.filterValueDataStoreKey]);
            var filterValue = row[column.dropDown.filter.dataStoreKey];
            if (filterValue) {
                if (Array.isArray(filterValue)) {
                    return filterValue.includes(
                        item[column.dropDown.filter.filterValueDataStoreKey],
                    );
                } else {
                    return (
                        item[column.dropDown.filter.filterValueDataStoreKey] ===
                        filterValue
                    );
                }
            }
        }
        return true;
    }

    private scrollStep = (ts: number) => {
        const el = this.matTableEl?.nativeElement;
        if (!el) return;

        if (this.waiting) {
            this.lastTs = ts;
            this.rafId = requestAnimationFrame(this.scrollStep);
            return;
        }

        const delta = (ts - this.lastTs) / 1000;
        this.lastTs = ts;

        const maxScroll = el.scrollHeight - el.clientHeight;
        if (maxScroll <= 0) {
            this.rafId = requestAnimationFrame(this.scrollStep);
            return;
        }

        el.scrollTop = Math.min(
            el.scrollTop + delta * this.speedPxPerSec,
            maxScroll,
        );

        if (el.scrollTop >= maxScroll - 0.5) {
            // Ende erreicht: 2s warten, dann zurück auf 0 und nochmals 2s warten bevor neu gestartet wird
            this.waiting = true;
            setTimeout(() => {
                el.scrollTop = 0;
                // zusätzliche 2s am Anfang warten
                setTimeout(() => {
                    this.lastTs = performance.now();
                    this.waiting = false;
                }, this.autoScrollTimeInMilliSeconds);
            }, this.autoScrollTimeInMilliSeconds);
        }

        this.rafId = requestAnimationFrame(this.scrollStep);
    };
    // Datasource sortierung Beispiel
    // https://snyk.io/advisor/npm-package/@angular/material/functions/@angular%2Fmaterial.MatTableDataSource
}
